import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from "@/components/ui/toaster";
import { ThemeProvider } from '@/components/theme-provider';

// Pages
import Dashboard from './pages/Dashboard';
import NotFound from './pages/NotFound';
import Auth from './pages/Auth';
import ResetPassword from './pages/ResetPassword';
import SmsCampaigns from './pages/SmsCampaigns';
import EmailCampaigns from './pages/EmailCampaigns';
import CampaignCreate from './pages/CampaignCreate';
import CampaignDetails from './pages/CampaignDetails';
import CustomersPage from './pages/Customers';
import RestaurantsPage from './pages/admin/Restaurants';
import SettingsPage from './pages/Settings';
import OrganisationPage from './pages/Organisation';
import CustomerProfile from './pages/CustomerProfile';
import OnboardingPage from './pages/Onboarding';
import OrderingPage from './pages/admin/OrderingPage';
import MediaLibrary from './pages/MediaLibrary';

// Admin pages
import SuperAdmin from './pages/admin/SuperAdmin';

// Components and utilities
import { RouteGuard } from './components/RouteGuard';
import { debugAuthState } from './utils/jwt-roles';
import { UserRole } from './context/auth/types';

// Import all required providers
import { SmsStatusProvider } from './context/SmsStatusContext';
import { AuthProvider } from './context/auth';
import { RestaurantProvider } from './context/RestaurantContext';
import { SmsBillingProvider } from './context/SmsBillingContext';
import { useAuth } from './context/auth';

// Create AppRoutes component that uses the auth context
const AppRoutes: React.FC = () => {
  const { user, isAuthenticated, loading } = useAuth();
  
  // Add a small effect to log authentication state
  useEffect(() => {
    console.log('AppRoutes: Auth state update', { 
      isAuthenticated, 
      hasUser: !!user, 
      loading,
      userId: user?.id,
      userRole: user?.role
    });
    
    // Run auth state debug when auth state changes
    if (isAuthenticated && !loading) {
      debugAuthState();
    }
  }, [isAuthenticated, user, loading]);
  
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/auth" element={<Auth />} />
      <Route path="/auth/reset-password" element={<ResetPassword />} />
      
      {/* Onboarding routes - for new sign-ups and in-progress users */}
      <Route element={<RouteGuard requiredRole={UserRole.NEW_SIGN_UP} allowedRoles={[UserRole.NEW_SIGN_UP, UserRole.IN_PROGRESS]} />}>
        <Route path="/onboarding" element={<OnboardingPage />} />
      </Route>
      
      {/* Protected routes with base user access */}
      <Route element={<RouteGuard requiredRole={UserRole.USER} />}>
        {/* Dashboard routes */}
        <Route path="/" element={<Dashboard />} />
        <Route path="/dashboard" element={<Dashboard />} />
        
        {/* Common feature routes - accessible to all authenticated users */}
        <Route path="/reports/*" element={<Navigate to="/dashboard" replace />} />
        <Route path="/analytics/*" element={<Navigate to="/dashboard" replace />} />
        <Route path="/loyalty-programs/*" element={<Navigate to="/dashboard" replace />} />
        
        {/* SMS Campaigns routes */}
        <Route path="/sms-campaigns" element={<SmsCampaigns />} />
        <Route path="/sms-campaigns/create" element={<CampaignCreate />} />
        <Route path="/campaigns/:id" element={<CampaignDetails />} />

        {/* Email Campaigns routes */}
        <Route path="/email-campaigns" element={<EmailCampaigns />} />

        {/* Media Library routes */}
        <Route path="/media" element={<MediaLibrary />} />
      </Route>
      
      {/* Restaurant admin routes */}
      <Route element={<RouteGuard requiredRole={UserRole.REST_ADMIN} />}>
        <Route path="/admin/ordering-page" element={<OrderingPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>
      
      {/* Organisation admin routes */}
      <Route element={<RouteGuard requiredRole={UserRole.ORG_ADMIN} />}>
        <Route path="/restaurants" element={<RestaurantsPage />} />
        <Route path="/organisation" element={<OrganisationPage />} />
      </Route>
      
      {/* Super admin routes */}
      <Route element={<RouteGuard requiredRole={UserRole.SUPER_ADMIN} />}>
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/customers/:customerId" element={<CustomerProfile />} />
        <Route path="/admin/super" element={<SuperAdmin />} />
      </Route>
      
      {/* Not Found - catch all route */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

// Main App component with provider hierarchy
function App() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: 2, // Increase retry attempts for database operations
        staleTime: 10 * 1000, // 10 seconds
      },
    },
  });

  // Add startup debugging
  useEffect(() => {
    debugAuthState();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system" storageKey="ui-theme">
        <AuthProvider>
          <RestaurantProvider>
            <SmsStatusProvider>
              <SmsBillingProvider>
                <Toaster />
                <AppRoutes />
              </SmsBillingProvider>
            </SmsStatusProvider>
          </RestaurantProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
